import moment from 'moment';
import { exportRowsToExcel } from '../../../utils/excelExport';
import { GRID_LIST_FETCH_SIZE } from '../../../components/shared/agGridCells';
import { mapAuditFields, mapAuditExportFields } from '../../../lib/auditColumnDefs';
import { toApiEntityStatus } from '../../../utils/status';
import { ACQUIRER_PARTNER_TYPE } from '../constants/constants';
import { ACQUIRER_EXPORT_COLUMNS } from '../table/columnDefs';
import type {
  AcquirerColumnFilterKey,
  AcquirerColumnFilters,
  AcquirerGridRow,
  AcquirerListRequest,
  AcquirerSupportedCurrency,
} from '../models/types';

export function normalizeSupportedCurrencies(raw: unknown): AcquirerSupportedCurrency[] {
  if (!Array.isArray(raw)) return [];

  const entries: AcquirerSupportedCurrency[] = [];
  const seenIds = new Set<number>();
  const seenCodes = new Set<string>();

  raw.forEach((item) => {
    if (item == null) return;

    if (typeof item === 'number' || (typeof item === 'string' && /^\d+$/.test(item.trim()))) {
      const countryId = Number(item);
      if (countryId > 0 && !seenIds.has(countryId)) {
        seenIds.add(countryId);
        entries.push({ currency: '', countryId });
      }
      return;
    }

    if (typeof item === 'string') {
      const currency = item.trim().toUpperCase();
      if (currency && !seenCodes.has(currency)) {
        seenCodes.add(currency);
        entries.push({ currency, countryId: 0 });
      }
      return;
    }

    if (typeof item === 'object') {
      const row = item as Record<string, unknown>;
      const currency = String(row.currency ?? row.currencyCode ?? row.code ?? '')
        .trim()
        .toUpperCase();
      const countryId = Number(row.countryId ?? row.autoId ?? row.id ?? 0);

      if (countryId > 0) {
        if (seenIds.has(countryId)) return;
        seenIds.add(countryId);
        entries.push({ currency, countryId });
        if (currency) seenCodes.add(currency);
        return;
      }

      if (currency && !seenCodes.has(currency)) {
        seenCodes.add(currency);
        entries.push({ currency, countryId: 0 });
      }
    }
  });

  return entries;
}

export function buildAcquirerGridRows(
  acquirers: Array<{
    id: number;
    name?: string;
    country?: string;
    status?: string;
    supportedPaymentMethods?: string[];
    trafficTypes?: string[];
    supportedCurrencies?: string[];
    supportedCurrencyEntries?: AcquirerSupportedCurrency[];
    totalTerminals?: number;
    terminalNames?: string[];
    createdAt?: string;
    code?: string;
    industryType?: string;
  }>,
  accentColors: string[],
): AcquirerGridRow[] {
  return acquirers.map((acq, idx) => {
    const name = acq.name || 'Unknown';
    const supportedCurrencyEntries = acq.supportedCurrencyEntries || [];
    return {
      id: acq.id,
      name,
      country: acq.country || '—',
      status: acq.status || 'Inactive',
      supportedPaymentMethods: acq.supportedPaymentMethods || [],
      trafficTypes: acq.trafficTypes || [],
      supportedCurrencies: acq.supportedCurrencies || [],
      supportedCurrencyEntries,
      totalTerminals: acq.totalTerminals ?? 0,
      terminalNames: acq.terminalNames || [],
      createdAt: acq.createdAt,
      ...mapAuditFields(acq as Record<string, unknown>),
      accent: accentColors[idx % accentColors.length],
      code: acq.code ?? '—',
      industryType: acq.industryType?.trim() || ACQUIRER_PARTNER_TYPE,
    };
  });
}

export function normalizeAcquirer(acq: any) {
  const trafficTypes = Array.isArray(acq.trafficTypes)
    ? [...new Set(
        acq.trafficTypes
          .filter((t: any) => typeof t === 'string' || !t?.status || String(t.status).toUpperCase() === 'ACTIVE')
          .map((t: any) =>
            (typeof t === 'string'
              ? t
              : String(t.trafficType ?? t.name ?? t.type ?? t.trafficName ?? '')).trim(),
          )
          .filter(Boolean),
      )]
    : [];
  const legacyTraffic = acq.trafficType ?? acq.flows?.[0]?.flowType ?? '';
  return {
    ...acq,
    contactEmail: acq.contactEmail ?? acq.email ?? '',
    contactPhone: acq.contactPhone ?? acq.contactNumber ?? '',
    totalTerminals: Number(acq.totalTerminals ?? 0),
    terminalNames: Array.isArray(acq.terminalNames)
      ? acq.terminalNames.map((name: unknown) => String(name).trim()).filter(Boolean)
      : [],
    trafficTypes: trafficTypes.length > 0 ? trafficTypes : legacyTraffic ? [legacyTraffic] : [],
    ...(() => {
      const supportedCurrencyEntries = normalizeSupportedCurrencies(acq.supportedCurrencies);
      return {
        supportedCurrencyEntries,
        supportedCurrencies: supportedCurrencyEntries
          .map((entry) => entry.currency)
          .filter(Boolean),
      };
    })(),
    supportedPaymentMethods: Array.isArray(acq.supportedPaymentMethods)
      ? [...new Set(
          acq.supportedPaymentMethods
            .filter((p: any) => typeof p === 'string' || !p?.status || String(p.status).toUpperCase() === 'ACTIVE')
            .map((p: any) => (typeof p === 'string' ? p : p?.paymentMethod || ''))
            .filter(Boolean),
        )]
      : [],
    settlementDays: Number(acq.settlementDays ?? 0),
  };
}

export function formatAcquirerSince(createdAt?: string) {
  return createdAt && moment(createdAt).isValid()
    ? moment(createdAt).format('DD MMM YYYY')
    : 'N/A';
}

const ACQUIRER_COLUMN_FILTER_KEYS: Record<AcquirerColumnFilterKey, true> = {
  name: true,
  industryType: true,
  code: true,
  country: true,
  paymentMethods: true,
  status: true,
  trafficTypes: true,
  currencies: true,
  totalTerminals: true,
  terminalNames: true,
  createdAt: true,
};

/**
 * Translate AG Grid's native column filterModel into the same shape the Filters drawer
 * stores in `columnFilters` / feeds `buildAcquirersRequest` — so header-funnel filters and
 * the drawer both produce identical `request.filters` payloads.
 *
 * Text filters → `{ [colId]: [filterText] }` (drawer multi-select uses string[] too).
 * Date column (`agDateColumnFilter` on createdAt) → `createdAt: [from, to]` yyyy-MM-dd.
 */
export function mapAgGridFilterModelToAcquirerFilters(
  filterModel: Record<string, unknown> | null | undefined,
): Partial<AcquirerColumnFilters> {
  const columnFilters: Partial<AcquirerColumnFilters> = {};
  if (!filterModel) return columnFilters;

  for (const [colId, raw] of Object.entries(filterModel)) {
    if (!raw || typeof raw !== 'object') continue;
    const model = raw as {
      filter?: unknown;
      dateFrom?: string;
      dateTo?: string;
    };

    if (colId === 'createdAt') {
      if (typeof model.dateFrom === 'string' && model.dateFrom.length >= 10) {
        const from = model.dateFrom.slice(0, 10);
        const to = (model.dateTo ?? model.dateFrom).slice(0, 10);
        columnFilters.createdAt = [from, to];
      }
      continue;
    }

    if (typeof model.filter !== 'string' || !model.filter.trim()) continue;
    if (colId in ACQUIRER_COLUMN_FILTER_KEYS) {
      columnFilters[colId as AcquirerColumnFilterKey] = [model.filter];
    }
  }

  return columnFilters;
}

export function buildAcquirersRequest(options: {
  page?: number;
  size?: number;
  filterStatus: string;
  search: string;
  columnFilters: AcquirerColumnFilters;
  /** Maps currency code → country IDs so filters.currencies is sent as countryIds. */
  countryIdsByCurrency?: Record<string, number[]>;
  export?: boolean;
}): AcquirerListRequest {
  const request: AcquirerListRequest = {
    page: options.page ?? 0,
    size: options.size ?? GRID_LIST_FETCH_SIZE,
  };
  if (options.filterStatus !== 'All') {
    request.status = toApiEntityStatus(options.filterStatus);
  }
  const query = options.search.trim();
  if (query) request.search = query;
  const filters = Object.fromEntries(
    (Object.entries(options.columnFilters) as [AcquirerColumnFilterKey, string[] | null][])
      .filter(([, values]) => Boolean(values?.length))
      .map(([key, values]) => {
        if (key === 'createdAt' && values) {
          return [key, values.filter(Boolean)];
        }
        if (key === 'currencies' && values) {
          const countryIds = new Set<string>();
          values.forEach((value) => {
            const trimmed = value.trim();
            if (!trimmed) return;
            if (/^\d+$/.test(trimmed)) {
              countryIds.add(trimmed);
              return;
            }
            const ids = options.countryIdsByCurrency?.[trimmed.toUpperCase()] ?? [];
            ids.forEach((id) => {
              if (id > 0) countryIds.add(String(id));
            });
          });
          return [key, Array.from(countryIds)];
        }
        return [key, values as string[]];
      })
      .filter(([, values]) => Boolean((values as string[]).length)),
  );
  if (Object.keys(filters).length > 0) request.filters = filters;
  if (options.export) request.export = true;
  return request;
}

export function mapAcquirerExportRow(acq: AcquirerGridRow) {
  const totalTerminals = Number(acq.totalTerminals ?? 0);
  const trafficTypes = (acq.trafficTypes || []).join('; ');

  return {
    name: String(acq.name || 'Unknown'),
    industryType: String(acq.industryType || ACQUIRER_PARTNER_TYPE),
    code: String(acq.code || '—'),
    country: String(acq.country || '—'),
    paymentMethods: (acq.supportedPaymentMethods || []).join('; ') || '—',
    trafficTypes: trafficTypes || '—',
    currencies: (acq.supportedCurrencies || []).join('; ') || '—',
    totalTerminals: totalTerminals > 0 ? totalTerminals : '—',
    terminalNames: (acq.terminalNames || []).join('; ') || '—',
    ...mapAuditExportFields(acq),
    status: String(acq.status || '—'),
  };
}

export async function exportAcquirersExcel(rows: ReturnType<typeof mapAcquirerExportRow>[]) {
  await exportRowsToExcel(rows, ACQUIRER_EXPORT_COLUMNS, {
    sheetName: 'Acquirers',
    fileNamePrefix: 'acquirers',
  });
}

